import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';
import type { RefreshableQueryMeta } from '@/hooks/useGlobalRefresh';

import useFundContext from '@/contexts/Fund/useFundContext';
import Transaction from '@/models/Transaction';
import TransactionAdditionalInfo, { transactionAdditionalInfoUrl } from '@/models/TransactionAdditionalInfo';
import DataTable, { RowGroup } from '@/components/ui/DataTable/DataTable';
import SelectAllHeader from '@/components/ui/DataTable/SelectAllHeader';
import { Col, Cell } from '@/components/ui/DataTable/ColumnsHelpers';
import cellType from '@/components/ui/DataTable/CellTypes';
import useTableDensity from '@/hooks/useTableDensity';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import CustomDialog from '@/components/ui/Dialog/Dialog';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import TransactionRowActions from './TransactionRowActions';
import TransactionCard from './TransactionCard';
import BoardTabs from '@/components/ui/BoardTabs/BoardTabs';
import useBoardTabs from '@/components/ui/BoardTabs/useBoardTabs';
/*
import download from "@/assets/icons/download-file.svg";
import support from "@/assets/icons/issue-support.svg";
*/
import fileAttach from '@/assets/icons/file-attach.svg';
import FiltersArea from './FiltersArea';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import BankStandingOrdersBlade from './BankStandingOrdersBlade';
import ReceiptEmailDialog from './AdditionalInfo/ReceiptEmailDialog';
import { ReceiptToSendDtoOut, receiptsToSend, toReceiptToSend } from '@/models/out/ReceiptToSendDtoOut';
import SelectedTransactionsSummary from './SelectedTransactionsSummary';
import { exportToExcel } from '@/helpers/exportTableHelper';
import { exportRowsToPdf } from '@/helpers/exportPdfHelper';
import { transactionsPdfDocument } from './transactionsPdf';
import { OVERHEADS_TABLE_FEATURE_KEY } from '@/helpers/overheadFees';
import OverheadsDialog from '@/components/Overheads/OverheadsDialog';
import eyeIcon from '@/assets/icons/eye.svg';
import { formatDateShort, getCurrentDate, getDefaultTransactionsDateRange } from '@/helpers/dateHelper';
import { balanceNotComputedBefore, isFeeRow } from './runningBalance';
import { formatMoney } from './formatMoney';
import Alert from '@/components/ui/Alert';
import i18n from '@/i18n/i18n';
import { ANALYTICS_EVENTS } from '@/helpers/analyticsEvents';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import { TransactionDateCell, TransactionNameCell } from './transactionBoardCells';
import CalendarModeProvider from '@/contexts/CalendarMode/CalendarModeProvider';
import useUserPreference from '@/hooks/useUserPreference';
import useFutureTransactionSources, { FutureTransactionSource } from './useFutureTransactionSources';
import useFundAccess from '@/hooks/useFundAccess';
import {
  CURRENT_GROUP_ID,
  FUTURE_GROUP_ID,
  FUTURE_TRANSACTIONS_FEATURE_KEY,
  FUTURE_BANK_STANDING_ORDERS_FEATURE_KEY,
  bankStandingOrdersToFutureRows,
  FUTURE_WINDOW_DAYS,
  FutureCoverage,
  TransactionStatus,
  coverageShortfall,
  dedupeFutureRows,
  flagInsufficientCoverage,
  futureRowId,
  futureWindow,
  isCurrentRow,
  isFutureRow,
  isSyntheticRow,
  sortSoonestFirst,
  splitAptTransactions,
  supportRequestsToFutureRows,
  transactionStatus,
} from './futureTransactions';

/** The status column's words, shared by the export, the search and the row caption. */
const TRANSACTION_STATUS_KEYS: Record<TransactionStatus, string> = {
  current: 'transactionStatusCurrent',
  future: 'transactionStatusFuture',
  projected: 'transactionStatusProjected',
};

const DENSITY_STORAGE_KEY = 'transactionsTableDensity';

const transactionSkeletonConfig = {
  select: () => ({
    widths: ['16px'],
  }),
  date: () => ({
    widths: ['56px'],
  }),
  amount: () => ({
    widths: ['56px'],
  }),
  cumulativeAmount: () => ({
    widths: ['56px'],
  }),
  kind: () => ({
    widths: ['64px'],
  }),
  contactName: (isEvenRow: boolean) => ({
    widths: isEvenRow ? ['76%'] : ['42%'],
  }),
  paymentMethod: (isEvenRow: boolean) => ({
    widths: isEvenRow ? ['81px'] : ['40px'],
  }),
  receiptId: () => ({
    widths: ['132px'],
  }),
  rowActions: () => ({
    widths: ['104px'],
  }),
};

/**
 * True when the query names this transaction's amount. Matched as a number and
 * not as text, so "50" finds a 50.00 movement without also dragging in 150 and
 * 2504. Signs are ignored - an expense is shown as -20.00, and a coordinator
 * looking for it types 20. A movement in a foreign currency is also matched on
 * its ILS amount, the figure the board shows beside the original one.
 */
const matchesAmount = (transaction: Transaction, searchValue: string): boolean => {
  const digits = searchValue.replace(/[,\s₪$]/g, '');
  const asNumber = Number(digits);
  // Number('') is 0, so a query of nothing but a currency sign would match every zero-amount row.
  if (!digits || !Number.isFinite(asNumber)) return false;

  const target = Math.abs(asNumber);
  // A whole number also matches the agorot after it, because that is how the amount
  // is remembered: a donation of 1,250.50 is looked up as 1250.
  const matches = (amount: number): boolean => {
    const absolute = Math.abs(amount);
    return absolute === target || (Number.isInteger(target) && Math.trunc(absolute) === target);
  };

  return matches(transaction.amount) || (transaction.ilsAmount != null && matches(transaction.ilsAmount));
};

const transactionSearch: FilterFn<Transaction> = (row, _columnId, value) => {
  if (!value) return true;

  const searchValue = String(value).trim().toLowerCase();
  if (!searchValue) return true;

  const transaction = row.original;

  const searchableFields = [
    transaction.contactName,
    transaction.notes,
    // 0 is what a movement without a receipt carries, and it must not be searchable.
    transaction.receiptId ? String(transaction.receiptId) : null,
    // The movement's APT reference. On a credit card payment it holds the card's
    // last 4 digits - the same value the row's additional-info blade shows.
    transaction.donationReference,
    // HAT-1203: «באיחור» or «עתידית» typed into the box finds the rows the status column would show.
    transaction.future ? i18n.t(TRANSACTION_STATUS_KEYS[transactionStatus(transaction)]) : null,
  ];

  return searchableFields.some((field) => field?.toLowerCase().includes(searchValue)) || matchesAmount(transaction, searchValue);
};

type ReceiptAction = {
  id: number;
  action: 'download' | 'newTab';
  defaultReceipt: boolean;
} | null;

const ReceiptFile = ({ receiptAction, setReceiptAction }: { receiptAction: ReceiptAction; setReceiptAction: (action: ReceiptAction) => void }) => {
  const { t } = useTranslation();
  const handledKeyRef = useRef<string | null>(null);
  const { data: receiptFile, error: receiptError } = useGetAuthApi<ArrayBuffer>(
    receiptAction ? `receipts/${receiptAction.id}?defaultReceipt=${receiptAction.defaultReceipt}` : '',
    'arraybuffer'
  );

  useEffect(() => {
    if (!receiptAction) {
      handledKeyRef.current = null;
      return;
    }
    if (receiptError) {
      return;
    }
    if (!receiptFile) return;

    const key = `${receiptAction.id}:${receiptAction.action}:${receiptAction.defaultReceipt}`;
    if (handledKeyRef.current === key) return;
    handledKeyRef.current = key;

    if (receiptAction.action === 'download') {
      downloadPdf(receiptFile, `${receiptAction.id}.pdf`);
    } else {
      openPdfInNewTab(receiptFile);
    }
    setReceiptAction(null);
  }, [receiptFile, receiptAction, receiptError, setReceiptAction]);

  const openPdfInNewTab = (data: ArrayBuffer) => {
    const file = new Blob([data], { type: 'application/pdf' });
    const fileURL = window.URL.createObjectURL(file);
    const newTab = window.open(fileURL, '_blank');
    if (newTab) {
      newTab.focus();
    }
    setTimeout(() => window.URL.revokeObjectURL(fileURL), 1000);
  };

  const downloadPdf = (data: ArrayBuffer, filename: string) => {
    const file = new Blob([data], { type: 'application/pdf' });
    const fileURL = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = filename;
    link.click();
    link.remove();
    window.URL.revokeObjectURL(fileURL);
  };

  return (
    <>
      {receiptError && (
        <CustomDialog
          open={!!receiptError && receiptAction != null}
          setOpen={() => {
            setReceiptAction(null);
          }}
        >
          <img src={fileAttach} alt="" />
          <h2 className="heading-h2-bold">{t('receiptUnavailable')}</h2>
        </CustomDialog>
      )}
    </>
  );
};

const TransactionPage = () => {
  const { currentFund, balance } = useFundContext();
  const { t } = useTranslation();
  // Permission worlds (Druk, 22.10.2026): the server sends only the rows of the worlds this coordinator
  // sees, and a running balance over part of the money is no balance - so the column goes too.
  const { canView } = useFundAccess(currentFund?.id);
  const seesAllMoney = canView('Donations') && canView('Supports');
  const { captureEvent } = usePostHogIdentification();
  const [receiptAction, setReceiptAction] = useState<ReceiptAction>(null);
  const [selectedContactName, setSelectedContactName] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const { isSidebarDocked, isMobile } = useDeviceDetection();
  // HAT-1203 ships for the wide layout only: the phone table and the tablet cards have
  // no design for the group headers yet, so there the board stays as it was.
  // Behind the fund's feature flag until the office opens it; off, the board is exactly as it was.
  const hasFutureTransactions = useFeature(FUTURE_TRANSACTIONS_FEATURE_KEY);
  const showFutureTransactions = hasFutureTransactions && !isMobile;
  // HAT-1309: the projected bank standing-order rows inside the group, behind a flag of their own.
  const hasFutureBankStandingOrders = useFeature(FUTURE_BANK_STANDING_ORDERS_FEATURE_KEY);
  const showFutureBankStandingOrders = showFutureTransactions && hasFutureBankStandingOrders;
  // HAT-1493: the overheads table in a window, from the export menu. Druk: there and nowhere else on
  // this board - a fee per row stays in the Excel file only.
  const hasOverheadsTable = useFeature(OVERHEADS_TABLE_FEATURE_KEY);
  const [isOverheadsOpen, setIsOverheadsOpen] = useState(false);
  const navigate = useNavigate();
  // כשהטאבים מוצגים הטאב הפעיל הוא הכותרת (ר' useBoardTabs).
  const { isVisible: hasBoardTabs } = useBoardTabs();
  const hasInquiries = useInquiriesFeature();
  /*
   * רוחב עמודת הפעולות תלוי בדגל הפניות.
   *
   * כשהדגל כבוי OpenInquiryButton מחזיר null, אבל הרוחב הקבוע של העמודה נמדד
   * בזמנו כשהאייקון היה שם - ולכן נשארו 40px ריקים (34px כפתור + 6px רווח)
   * שדחפו את שאר האייקונים מהקצה. שתי המחרוזות כתובות במלואן כי Tailwind
   * מייצר מחלקות מהמקור ולא מערכים שמחושבים בזמן ריצה.
   */
  const rowActionsWidth = hasInquiries ? 'max-w-[124px] min-w-[124px] w-[124px]' : 'max-w-[84px] min-w-[84px] w-[84px]';
  // Mobile hides the "transaction type" column, so there the prefix is the only
  // place the kind is shown and it stays regardless of the chosen density.
  const showKindPrefix = !isSidebarDocked || density === 'airy';

  const [isSendReceiptsOpen, setIsSendReceiptsOpen] = useState<boolean>(false);
  const [receiptsToSendByEmail, setReceiptsToSendByEmail] = useState<ReceiptToSendDtoOut[]>([]);
  // Set only when a single row's receipt is sent, so the dialog can offer the donor's own address
  const [transactionToSend, setTransactionToSend] = useState<Transaction | null>(null);

  const [actionAlert, setActionAlert] = useState<string | null>(null);
  const [dateParams, setDateParams] = useState<{ start?: string; end?: string }>(getDefaultTransactionsDateRange());

  const {
    data: allTransactions,
    refetch,
    isLoading,
  } = useGetAuthApi<Transaction[]>(`funds/financial-data/${currentFund?.id}/transactions?start=${dateParams.start}&end=${dateParams.end}`, undefined, {
    /*
     * חלק מ"הנתונים של המסך" (ר' useGlobalRefresh): זה מה שכפתור הרענון מושך,
     * וזה גם מה שנמשך בחזרה ללשונית (ר' useFocusRefresh) - הצהרה אחת לשניהם.
     *
     * ⚠️ בלי `refetchOnWindowFocus` כאן. עד HAT-1315 הוא ישב על השאילתה הזו
     * לבדה, ולכן חזרה ללשונית משכה את הלוח אבל לא את היתרה שבפס העליון, והשעה
     * שליד הכפתור - הישן שבנתונים - לא זזה.
     */
    meta: { refreshable: true } satisfies RefreshableQueryMeta,
  });

  const { data: transactionToSendInfo } = useGetAuthApi<TransactionAdditionalInfo>(
    transactionToSend ? transactionAdditionalInfoUrl(transactionToSend) : ''
  );

  // HAT-1203: «תנועות עתידיות» — the two reads beside the board's own call. See the hook for why they are separate.
  const {
    aptTransactions: futureAptTransactions,
    supportRequests,
    commissionRules,
    bankStandingOrders,
    isLoading: isFutureSourcesLoading,
    failedSources: failedFutureSources,
    retryFailed: retryFailedFutureSources,
  } = useFutureTransactionSources(currentFund?.id, showFutureTransactions, showFutureBankStandingOrders && canView('Donations'), canView('Supports'));
  // The design opens with the group collapsed; the choice then follows the coordinator between visits.
  const [futureExpandedPreference, setFutureExpandedPreference] = useUserPreference('transactionsFutureExpanded');
  const isFutureCollapsed = futureExpandedPreference !== 'true';
  const [isCurrentCollapsed, setIsCurrentCollapsed] = useState(false);

  const [transactionsWithoutCommissions, setTransactionsWithoutCommissions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [matchesBoardFilters, setMatchesBoardFilters] = useState<(transaction: Transaction) => boolean>(() => () => true);
  // What the future group costs against what the fund can pay — null while nothing is outgoing.
  const [futureCoverage, setFutureCoverage] = useState<FutureCoverage | null>(null);

  //* Any code (if necessary) for the AdditionalInfo component goes below
  const [detailedTransaction, setDetailedTransaction] = useState<Transaction>();
  // HAT-1309: the list behind a bank standing-order row of «תנועות עתידיות».
  const [bankOrdersRow, setBankOrdersRow] = useState<Transaction>();

  //* Any code (if necessary) for the AdditionalInfo component goes above

  const filtersRef = useRef<{ resetFilters: () => void }>(null);
  const emptyStateSentRef = useRef(false);

  useEffect(() => {
    if (!allTransactions) {
      setTransactionsWithoutCommissions([]);
      setFilteredTransactions([]);
      return;
    }
    const withoutCommissions = (rows: Transaction[]) => rows.filter((transaction) => !isFeeRow(transaction));
    if (!showFutureTransactions) {
      const rows = withoutCommissions(allTransactions);
      setTransactionsWithoutCommissions(rows);
      setFilteredTransactions(rows);
      return;
    }

    const window = futureWindow(new Date());
    const fundId = currentFund?.id ?? 0;
    /*
     * A movement dated after today belongs to «עתידיות» whichever call returned it. The
     * board's own range normally ends today, but a range typed by hand can reach further,
     * and then the same post-dated details arrive twice — once per call — and would have
     * shown in both groups. The running balance is meaningless ahead of today, so it is
     * blanked; the settled rows' chain stays untouched.
     */
    const { current, future: mainFuture } = splitAptTransactions(withoutCommissions(allTransactions), window);
    const { future: aptFuture } = splitAptTransactions(withoutCommissions(futureAptTransactions ?? []), window);
    // One row per collection date for the bank standing orders (HAT-1309); the name carries the count.
    const bankRowLabel = (count: number) => (count === 1 ? t('futureBankStandingOrdersOne') : t('futureBankStandingOrdersMany', { count }));
    const futureRows = sortSoonestFirst(
      dedupeFutureRows([
        ...[...aptFuture, ...mainFuture].map((row) => ({ ...row, cumulativeAmount: 0 })),
        ...supportRequestsToFutureRows(supportRequests ?? [], fundId, window),
        ...bankStandingOrdersToFutureRows(bankStandingOrders ?? [], fundId, window, bankRowLabel),
      ])
    );
    const coverage = coverageShortfall(futureRows, balance, window, commissionRules ?? null);
    setFutureCoverage(coverage);
    const future = flagInsufficientCoverage(futureRows, coverage);
    // Both groups go through the same filters and search; the table pulls them apart again.
    const merged = [...future, ...current];
    setTransactionsWithoutCommissions(merged);
    setFilteredTransactions(merged);
  }, [allTransactions, futureAptTransactions, supportRequests, commissionRules, bankStandingOrders, balance, showFutureTransactions, currentFund?.id, t]);

  useEffect(() => {
    if (isLoading || !currentFund) return;
    if (filteredTransactions.length === 0) {
      if (emptyStateSentRef.current) return;
      emptyStateSentRef.current = true;
      captureEvent(ANALYTICS_EVENTS.TRANSACTIONS_EMPTY_STATE_SHOWN);
      return;
    }
    emptyStateSentRef.current = false;
    // captureEvent is recreated each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, currentFund, filteredTransactions.length]);

  useEffect(() => {
    refetch();
  }, [dateParams, refetch]);

  useEffect(() => {
    setDetailedTransaction(undefined);
    setFutureToggledThisVisit(null);
    filtersRef.current?.resetFilters();
  }, [currentFund]);

  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkExpanded = useRef(false);
  useEffect(() => {
    const resourceId = searchParams.get('resourceId');
    if (!resourceId || !allTransactions) return;
    // A post-dated movement (an instalment, a cheque) lives in the future call's rows, not the board's own.
    const tx = [...allTransactions, ...(futureAptTransactions ?? [])].find((t) => String(t.transactionId) === resourceId);
    if (tx) {
      setDetailedTransaction(tx);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('resourceId');
          return next;
        },
        { replace: true }
      );
    } else if (!deepLinkExpanded.current) {
      deepLinkExpanded.current = true;
      setDateParams({ start: '2015-01-01', end: getCurrentDate() });
    } else {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('resourceId');
          return next;
        },
        { replace: true }
      );
    }
  }, [allTransactions, futureAptTransactions]);

  const exportSelectedTransactions = (selectedTransactions: Transaction[]) => {
    captureEvent(ANALYTICS_EVENTS.TRANSACTIONS_EXPORT_CLICKED);
    exportToExcel(selectedTransactions, exportColumns, t('transactionsBoard'), t);
  };

  // Druk (question 8): the sheet carries the future rows too, told apart by the status column.
  // The board's own call has only the settled ones, so the future rows are added in front.
  // HAT-1448: the settled rows come from allTransactions, fee movements included - management
  // keeps the fees off the board, so the sheet is the one place they show.
  // HAT-1470: and the board's filters hold for the sheet as well.
  const boardExportData = (
    showFutureTransactions ? [...transactionsWithoutCommissions.filter(isFutureRow), ...(allTransactions ?? [])] : (allTransactions ?? [])
  ).filter(matchesBoardFilters);

  // "Not computed before 1.8.2025" for a row older than the running balance, rather than a bare dash.
  const balanceNote = (row: Transaction): string | undefined => {
    const from = balanceNotComputedBefore(row, balance?.runningBalanceFrom);
    return from ? t('cumulativeAmountNotComputedBefore', { date: formatDateShort(from) }) : undefined;
  };

  const openSendReceiptsDialog = (transactions: Transaction[]) => {
    const receipts = receiptsToSend(transactions);

    if (receipts.length === 0) {
      setActionAlert(t('noReceiptsToSend'));
      return;
    }
    setActionAlert(null);

    setTransactionToSend(null);
    setReceiptsToSendByEmail(receipts);
    setIsSendReceiptsOpen(true);
  };

  const openSendReceiptDialog = (transaction: Transaction) => {
    if (!transaction.receiptId) {
      return;
    }

    setTransactionToSend(transaction);
    setReceiptsToSendByEmail([toReceiptToSend(transaction)]);
    setIsSendReceiptsOpen(true);
  };

  const downloadReceipt = (receiptId: number, defaultReceipt?: boolean) => {
    if (receiptId) {
      setReceiptAction({ id: receiptId, action: 'download', defaultReceipt: defaultReceipt ?? true });
    }
  };

  const issueSupport = (transaction: Transaction) => {
    console.log('issueSupport', transaction);
  };

  /*
  const exportReceipt = (transaction: Transaction) => {
    console.log('exportReceipt', transaction);
    if (transaction.receiptId) {
      setReceiptAction({ id: transaction.receiptId, action: 'download' });
    }
  };
  */

  /*
   * HAT-1203: a click opens whatever the row really is. A settled movement and a future
   * accounting row have a details blade; a support month is a support request, so it
   * opens on that board. A projected bank standing-order row stands for every order
   * collected that day, and opens the whole list of them (HAT-1309).
   */
  const openTransaction = (transaction: Transaction) => {
    const meta = transaction.future;
    if (meta?.source === 'support' && currentFund) {
      navigate(`/${currentFund.id}/support/support-requests?resourceId=${meta.correlationId}`);
      return;
    }
    if (meta?.source === 'standingOrder') {
      setDetailedTransaction(undefined);
      setBankOrdersRow(transaction);
      return;
    }
    setBankOrdersRow(undefined);
    setDetailedTransaction(transaction);
  };

  const futureCount = useMemo(() => filteredTransactions.filter(isFutureRow).length, [filteredTransactions]);
  const currentCount = useMemo(() => filteredTransactions.filter(isCurrentRow).length, [filteredTransactions]);
  const futureCountBeforeFilters = useMemo(() => transactionsWithoutCommissions.filter(isFutureRow).length, [transactionsWithoutCommissions]);
  const currentCountBeforeFilters = useMemo(() => transactionsWithoutCommissions.filter(isCurrentRow).length, [transactionsWithoutCommissions]);
  const isFutureLoading = showFutureTransactions && isFutureSourcesLoading;
  // The spinner says "rows may still arrive". Past ten seconds that is no longer useful information —
  // whatever turns up later is simply added — and a ring that never stops reads as a fault.
  const [futureLoadingTimedOut, setFutureLoadingTimedOut] = useState(false);
  useEffect(() => {
    if (!isFutureLoading) {
      setFutureLoadingTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setFutureLoadingTimedOut(true), 10_000);
    return () => clearTimeout(timer);
  }, [isFutureLoading]);
  const showFutureLoading = isFutureLoading && !futureLoadingTimedOut;
  const failedSourceLabels: Record<FutureTransactionSource, string> = {
    aptTransactions: 'futureAptFailed',
    supportRequests: 'futureSupportsFailed',
    commissionRules: 'futureRulesFailed',
    bankStandingOrders: 'futureBankStandingOrdersFailed',
  };
  /*
   * Druk (15.9.2026): the alert says the sums, not just that there is a problem — what the
   * supports come to with their commissions, and what has to be deposited to cover them,
   * the deposit's own commission included.
   */
  const futureAlertText = useMemo(() => {
    if (!futureCoverage || futureCoverage.shortfall <= 0) return undefined;
    const currency = balance?.currency;
    const lines = [t('futureBalanceAlert', { total: formatMoney(futureCoverage.total, currency), missing: formatMoney(futureCoverage.deposit, currency) })];
    // Druk (16.9.2026): one short sentence that the overheads are estimated in, without their sum.
    if (futureCoverage.fees > 0 || futureCoverage.depositPercent > 0) lines.push(t('futureBalanceAlertFees'));
    lines.push(t('futureBalanceAlertEstimate'));
    return lines.join('\n');
  }, [futureCoverage, balance?.currency, t]);
  // The failure names its source because the coordinator's next step depends on it — supports that
  // did not load mean approved supports may be missing; scheduled movements are another matter.
  const futureNote = failedFutureSources.length ? (
    <>
      {failedFutureSources.map((source) => t(failedSourceLabels[source])).join(' · ')}{' '}
      <button type="button" className="body-md-link text-(--font-body-1)" onClick={retryFailedFutureSources}>
        {t('tryAgain')}
      </button>
    </>
  ) : undefined;
  /*
   * The board never opens onto nothing: with no settled rows to show, the future group opens
   * itself, and the saved preference is left alone — that is the board's call. A click on the
   * header then wins for the rest of the visit, or the toggle would look broken when the
   * coordinator collapses a group the board keeps forcing open.
   */
  const [futureToggledThisVisit, setFutureToggledThisVisit] = useState<boolean | null>(null);
  const isFutureCollapsedNow = futureToggledThisVisit ?? (isFutureCollapsed && currentCount > 0);

  const rowGroups = useMemo<RowGroup<Transaction>[]>(() => {
    const filtersHideFuture = futureCountBeforeFilters > 0 && futureCount === 0;
    const filtersHideCurrent = currentCountBeforeFilters > 0 && currentCount === 0;
    return [
      {
        id: FUTURE_GROUP_ID,
        // Figma «Group Folder»: the window is part of the name, and it is Druk's fixed week.
        label: t('futureTransactionsGroup', { days: FUTURE_WINDOW_DAYS }),
        count: futureCount,
        loading: showFutureLoading,
        loadingLabel: t('futureTransactionsUpdating'),
        collapsed: isFutureCollapsedNow,
        onToggle: () => {
          setFutureToggledThisVisit(!isFutureCollapsedNow);
          setFutureExpandedPreference(isFutureCollapsedNow ? 'true' : 'false');
        },
        contains: isFutureRow,
        flagged: filteredTransactions.some((row) => row.future?.alert),
        flagTitle: futureAlertText,
        emptyMessage: filtersHideFuture ? t('futureTransactionsNoMatch') : t('futureTransactionsEmpty', { days: FUTURE_WINDOW_DAYS }),
        noMatchMessage: t('futureTransactionsNoMatch'),
        note: futureNote,
      },
      {
        id: CURRENT_GROUP_ID,
        label: t('currentTransactionsGroup'),
        count: currentCount,
        collapsed: isCurrentCollapsed,
        onToggle: () => setIsCurrentCollapsed((value) => !value),
        contains: isCurrentRow,
        emptyMessage: filtersHideCurrent ? t('currentTransactionsNoMatch') : t('currentTransactionsEmpty'),
        noMatchMessage: t('currentTransactionsNoMatch'),
      },
    ];
  }, [
    filteredTransactions,
    futureCount,
    currentCount,
    futureCountBeforeFilters,
    currentCountBeforeFilters,
    showFutureLoading,
    isFutureCollapsedNow,
    isCurrentCollapsed,
    futureNote,
    futureAlertText,
    setFutureExpandedPreference,
    t,
  ]);

  // A collapsed group keeps its header but its rows leave the table, so "select all"
  // and the selection summary can only ever speak about what is on screen.
  const visibleTransactions = showFutureTransactions
    ? filteredTransactions.filter((row) => (isFutureRow(row) ? !isFutureCollapsedNow : !isCurrentCollapsed))
    : filteredTransactions;

  // Cell() reads translation hooks, so call it on every render even when the
  // mobile date component is selected. Resizing must not change hook order.
  const desktopDateCell = Cell<Transaction>(cellType.Date, { dualCalendar: true });
  const allColumns: ColumnDef<Transaction>[] = [
    {
      id: 'select',
      header: ({ table }) => <SelectAllHeader table={table} />,
      cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} />,
      meta: {
        className: 'w-[36px]',
        hiddenOnMobile: true,
        lockLayout: true,
      },
    },
    {
      id: 'date',
      header: Col(t('date')),
      accessorKey: 'date',
      cell: isSidebarDocked ? desktopDateCell : TransactionDateCell,
      meta: {
        className: 'w-[112px]',
        mobileClassName: 'w-[27%]',
      },
    },
    {
      id: 'amount',
      header: Col(t('amount')),
      accessorFn: (row) => ({
        amount: row.amount,
        currency: row.currency,
        ilsAmount: row.ilsAmount,
        exchangeRate: row.exchangeRate,
        fundCurrency: balance?.currency,
        future: row.future
          ? {
              alert: row.future.alert,
              alertText: futureAlertText,
              // A caption only where the row does not already say it: a charge that is a projection,
              // not a record.
              caption: row.future.projected ? t('futurePendingExecution') : undefined,
            }
          : undefined,
      }),
      cell: Cell(cellType.Amount),
      sortingFn: (a, b) => a.original.amount - b.original.amount,
      meta: {
        className: 'w-[144px]',
        mobileClassName: 'w-[39%]',
      },
    },
    {
      // HAT-1436: "כמה נשאר בקופה" after this row's movement - missing until now, the
      // reporter's own words. Next to the amount, not inside it, because it answers a
      // different question (the running total, not this row's figure).
      id: 'cumulativeAmount',
      header: (ctx) => <span title={t('cumulativeAmountTooltip')}>{Col<Transaction>(t('cumulativeAmount'))(ctx)}</span>,
      accessorFn: (row) => ({
        // null on a row the server never computed a real balance for: one of «תנועות
        // עתידיות» (futureTransactions.ts seeds cumulativeAmount at 0 for all of them),
        // a movement from before the running balance started counting commissions
        // (NewAptFundsFinancialDataService leaves it at its decimal default, also 0),
        // or a caller that omits the field entirely. Dashboard's flow chart treats a
        // zero the same way (useDashboardData.ts) - a dash here, rather than a
        // balance nobody actually had.
        value: isFutureRow(row) || !row.cumulativeAmount ? null : row.cumulativeAmount,
        currency: balance?.currency || row.currency,
        note: balanceNote(row),
      }),
      cell: Cell(cellType.Balance),
      sortingFn: (a, b) => a.original.cumulativeAmount - b.original.cumulativeAmount,
      meta: {
        className: 'w-[128px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'kind',
      header: Col(t('transactionType')),
      accessorKey: 'kind',
      cell: Cell(cellType.TransactionType),
      meta: {
        className: 'w-[112px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'contactName',
      header: Col(t('transactionDetails')),
      accessorFn: (row) => ({ kind: row.kind, name: row.contactName }),
      cell: TransactionNameCell,
      sortingFn: (a, b) => (a.original.contactName ?? '').localeCompare(b.original.contactName ?? ''),
      meta: {
        // min-w-0 lets ellipsis work; w-auto keeps this column on the leftover width
        // once fixed neighbours (receipt, payment, actions) claim their mins.
        className: 'min-w-0 w-auto',
        minLayoutWidthPx: 160,
        mobileClassName: 'w-[34%]',
        showKindPrefix,
        isSidebarDocked,
        onOpen: openTransaction,
      },
    },
    {
      id: 'paymentMethod',
      header: Col(t('paymentMethod')),
      accessorKey: 'paymentMethod',
      cell: Cell(cellType.PaymentMethod),
      meta: {
        className: 'w-[128px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'receiptId',
      header: Col(t('receipt')),
      accessorFn: (row) => ({
        kind: row.kind,
        receiptId: row.receiptId,
        future: Boolean(row.future),
        onClick: () => {
          if (row.receiptId) {
            setReceiptAction({ id: row.receiptId, action: 'newTab', defaultReceipt: row.defaultReceipt });
          }
        },
      }),
      cell: Cell(cellType.Receipt),
      sortingFn: (a, b) => (a.original.receiptId || 0) - (b.original.receiptId || 0),
      meta: {
        // Wide enough for the Hebrew status label (t('noReceipt') → "עדיין לא הונפקה").
        className: 'min-w-[132px] w-[132px]',
        minLayoutWidthPx: 132,
        hiddenOnMobile: true,
      },
    },
    /*
    {
      id: 'notes',
      header: Col(t('notes')),
      accessorKey: 'monthlyCommission',
      cell: Cell(cellType.Notes),
      meta: {
        className: 'max-w-[27%] min-w-[27%] w-[27%]',
      },
    },
    */
    /*
    {
      id: 'actions',
      header: () => null,
      accessorFn: (row) => ({
        items: [
          {
            icon: download,
            text: t("exportTransactionDetails"),
            onClick: () => exportTransaction([row]),
          },
          {
            icon: support,
            text: t('issueSupportCertificate'),
            onClick: () => issueSupport(row),
          },
          {
            icon: download,
            text: t('exportReceipt'),
            onClick: () => exportReceipt(row),
          },
        ],
      }),
      cell: Cell(cellType.Menu),
      meta: {
        className: 'max-w-[2.65%] min-w-[2.65%] w-[2.65%]',
      },
    },
*/
    {
      id: 'rowActions',
      header: '',
      enableSorting: false,
      // A support month or a projected charge is not a movement: no receipt, nothing to enquire about.
      cell: ({ row }) =>
        row.original.future && row.original.future.source !== 'apt' ? null : (
          <TransactionRowActions
            transaction={row.original}
            onDownloadReceipt={(transaction) => downloadReceipt(transaction.receiptId, transaction.defaultReceipt)}
            onSendReceiptEmail={openSendReceiptDialog}
          />
        ),
      meta: {
        className: rowActionsWidth,
        hiddenOnMobile: true,
      },
    },
  ];

  const columns = seesAllMoney ? allColumns : allColumns.filter((column) => column.id !== 'cumulativeAmount');

  const allExportColumns: ColumnDef<Transaction>[] = [
    {
      id: 'date',
      header: Col(t('date')),
      accessorKey: 'date',
    },
    {
      // HAT-1203: a sheet where money that moved and money that has not look the same is a
      // reconciliation trap, so every row says which it is.
      id: 'status',
      header: Col(t('transactionStatusColumn')),
      accessorFn: (row) => t(TRANSACTION_STATUS_KEYS[transactionStatus(row)]),
    },
    {
      id: 'amount',
      header: Col(t('amount')),
      accessorKey: 'amount',
      accessorFn: (row) =>
        row.amount.toLocaleString(i18n.language, {
          style: 'currency',
          currency: row.currency || 'ILS',
        }),
    },
    {
      id: 'currency',
      header: Col(t('currency')),
      accessorFn: (row) => {
        const cur = row.currency || 'ILS';
        const formatter = new Intl.NumberFormat(i18n.language, {
          style: 'currency',
          currency: cur,
        });
        return formatter.formatToParts(0).find((part) => part.type === 'currency')?.value || cur;
      },
    },
    {
      id: 'exchangeRate',
      header: Col(t('exchangeRate')),
      accessorFn: (row) => {
        const exchangeRate = row.exchangeRate || (row.currency === 'ILS' ? 1 : null);
        return exchangeRate ? exchangeRate.toFixed(2) : '';
      },
    },
    {
      id: 'totalAmount',
      header: Col(t('totalAmount')),
      accessorFn: (row) => {
        const totalAmount = row.currency === 'ILS' ? row.amount : (row.ilsAmount ?? null);
        return totalAmount
          ? totalAmount.toLocaleString(i18n.language, {
              style: 'currency',
              currency: 'ILS',
            })
          : '';
      },
    },
    {
      id: 'cumulativeAmount',
      header: Col(t('cumulativeAmount')),
      // HAT-1436: same "no real balance computed" rows as the on-screen column - a
      // blank cell, not a balance of zero nobody actually had.
      accessorFn: (row) =>
        isFutureRow(row) || !row.cumulativeAmount
          ? ''
          : row.cumulativeAmount.toLocaleString(i18n.language, {
              style: 'currency',
              currency: balance?.currency || row.currency || 'ILS',
            }),
    },
    {
      id: 'kind',
      header: Col(t('transactionType')),
      accessorKey: 'kind',
    },
    {
      id: 'contactName',
      header: Col(t('transactionDetails')),
      accessorKey: 'contactName',
    },
    {
      id: 'contactIsraeliId',
      header: Col(t('contactIsraeliId')),
      accessorKey: 'contactIsraeliId',
    },
    {
      id: 'contactId',
      header: Col(t('contactId')),
      // HAT-1203: a row the board made up (a support month, a projected charge) has no movement,
      // so its ids and commissions export as blanks rather than zeros a reader might take for values.
      accessorFn: (row) => (isSyntheticRow(row) || !row.contactId ? '' : row.contactId),
    },
    {
      id: 'paymentMethod',
      header: Col(t('paymentMethod')),
      accessorFn: (row) => t(row.paymentMethod),
    },
    {
      id: 'receiptId',
      header: Col(t('receipt')),
      accessorFn: (row) => (row.receiptId ? row.receiptId : ''),
    },
    {
      id: 'transactionId',
      header: Col(t('transactionId')),
      accessorFn: (row) => (isSyntheticRow(row) ? '' : row.transactionId),
    },
    {
      id: 'transactionDetailId',
      header: Col(t('transactionDetailId')),
      accessorFn: (row) => (isSyntheticRow(row) ? '' : row.transactionDetailId),
    },
    {
      id: 'commissionAmount',
      header: Col(t('commissionAmount')),
      accessorFn: (row) =>
        isSyntheticRow(row)
          ? ''
          : row.commissionAmount.toLocaleString(i18n.language, {
              style: 'currency',
              currency: row.currency || 'ILS',
            }),
    },
    {
      id: 'commissionType',
      header: Col(t('commissionType')),
      accessorKey: 'commissionType',
    },
    {
      id: 'additionalCommissionAmount',
      header: Col(t('additionalCommissionAmount')),
      accessorFn: (row) =>
        isSyntheticRow(row)
          ? ''
          : row.additionalCommissionAmount.toLocaleString(i18n.language, {
              style: 'currency',
              currency: row.currency || 'ILS',
            }),
    },
    {
      id: 'additionalCommissionType',
      header: Col(t('additionalCommissionType')),
      accessorKey: 'additionalCommissionType',
    },
  ];

  const exportColumns = seesAllMoney ? allExportColumns : allExportColumns.filter((column) => column.id !== 'cumulativeAmount');

  //* Any code (if necessary) for the FiltersArea component goes below
  // Do not mind if you have to create a new state etc., just add it below.
  // Moving them upwards will be done in a separate step
  const onDateRangeChange = (from?: string, to?: string) => {
    setDateParams({ start: from, end: to });
  };
  //* Any code (if necessary) for the FiltersArea component goes above

  return (
    <div>
      {currentFund ? (
        <>
          {/* ניווט הלוחות במסך צר, בשפת נדרים. מחזיר null מעל 1100. */}
          <BoardTabs />
          {actionAlert && <Alert type="error" message={actionAlert} onClose={() => setActionAlert(null)} />}
          {detailedTransaction && (
            <AdditionalInfo
              transaction={detailedTransaction}
              handleXclick={() => setDetailedTransaction(undefined)}
              exportTransaction={() => exportSelectedTransactions(boardExportData)}
              issueSupport={issueSupport}
              downloadReceipt={downloadReceipt}
              onContactNameClick={setSelectedContactName}
            />
          )}
          {bankOrdersRow && <BankStandingOrdersBlade transaction={bankOrdersRow} handleXclick={() => setBankOrdersRow(undefined)} />}
          {hasOverheadsTable && <OverheadsDialog open={isOverheadsOpen} onClose={() => setIsOverheadsOpen(false)} />}

          <DataTable
            scrollMode="document"
            mobileLayout="transactions"
            minTableWidth={1000}
            title={t('transactionsBoard')}
            data={visibleTransactions}
            getRowId={futureRowId}
            rowGroups={showFutureTransactions ? rowGroups : undefined}
            isLoading={isLoading}
            columns={columns}
            exportColumns={exportColumns}
            exportData={boardExportData}
            onExport={(format) => captureEvent(ANALYTICS_EVENTS.TRANSACTIONS_EXPORT_CLICKED, { format })}
            // HAT-1399: unlike the sheet, the PDF is what the board shows - its rows, its columns.
            exportPdf={(sections) =>
              exportRowsToPdf(
                transactionsPdfDocument({
                  sections,
                  fund: currentFund,
                  dateRange: dateParams,
                  fundCurrency: balance?.currency,
                  isFiltered: searchQuery.trim() !== '' || filteredTransactions.length !== transactionsWithoutCommissions.length,
                  withRunningBalance: seesAllMoney,
                  t,
                  language: i18n.language,
                })
              )
            }
            exportExtraItems={
              hasOverheadsTable
                ? [{ key: 'overheads', label: t('overheadsTable'), icon: eyeIcon, onSelect: () => setIsOverheadsOpen(true), announce: OVERHEADS_TABLE_FEATURE_KEY }]
                : undefined
            }
            skeletonConfig={transactionSkeletonConfig}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            density={density}
            onDensityChange={setDensity}
            showCalendarMode
            columnLayoutKey="transactions"
            searchPlaceholder="searchTransaction"
            searchTooltip="searchTransactionTooltip"
            filters={
              <FiltersArea
                transactions={transactionsWithoutCommissions ?? []}
                setFilteredTransactions={setFilteredTransactions}
                setRowFilter={setMatchesBoardFilters}
                onDateRangeChange={onDateRangeChange}
                dateRange={dateParams}
                selectedContactName={selectedContactName}
                setSelectedContactName={setSelectedContactName}
                ref={filtersRef}
                isBusy={isLoading}
                showStatusFilter={showFutureTransactions}
              />
            }
            handleRowClick={openTransaction}
            /*
             * תצוגת המסך הצר. הכרטיס מקבל את אותה שורה שהטבלה הייתה
             * מקבלת, ומחזיר את חמש העמודות שמסומנות hiddenOnMobile —
             * ר' TransactionCard.
             */
            hideTitleOnCards={hasBoardTabs}
            cardLayout="flat"
            renderCard={(row, { selectionMode }) => (
              <TransactionCard
                row={row}
                selectionMode={selectionMode}
                isActive={row.original === detailedTransaction}
                fundCurrency={balance?.currency}
                runningBalanceFrom={balance?.runningBalanceFrom}
                onOpen={openTransaction}
                onDownloadReceipt={(transaction) => downloadReceipt(transaction.receiptId, transaction.defaultReceipt)}
                onSendReceiptEmail={openSendReceiptDialog}
                onViewReceipt={(transaction) => setReceiptAction({ id: transaction.receiptId, action: 'newTab', defaultReceipt: transaction.defaultReceipt })}
              />
            )}
            selectionActions={[
              /*
              {
                label: t('exportReceipts'),
                onClick: (transactions) => {
                  transactions.forEach((t) => {
                    exportReceipt(t);
                  });
                },
              },
              {
                label: t('issueSupportCertificates'),
                onClick: (transactions) => {
                  transactions.forEach((t) => {
                    issueSupport(t);
                  });
                },
              },
              */
              {
                label: t('sendReceipts'),
                onClick: (transactions) => {
                  openSendReceiptsDialog(transactions);
                },
                isVisible: (selectedRows: Transaction[]) => selectedRows.some((row) => row.kind === 'תרומה' && row.receiptId),
              },
              {
                label: t('exportTransactionsDetails'),
                onClick: (transactions) => {
                  exportSelectedTransactions(transactions);
                },
              },
            ]}
            selectionSummary={(selectedTransactions) => <SelectedTransactionsSummary transactions={selectedTransactions} fundCurrency={balance?.currency} />}
            activeRecord={detailedTransaction}
            customGlobalSearch={transactionSearch}
            emptyStateContent={{ title: t('noTransactionsFound'), description: t('noActivityFund') }}
          />
        </>
      ) : (
        <h1>{t('noFundSelected')}.</h1>
      )}
      {receiptAction && <ReceiptFile receiptAction={receiptAction} setReceiptAction={setReceiptAction} />}
      <ReceiptEmailDialog
        receipts={receiptsToSendByEmail}
        defaultEmail={transactionToSend ? transactionToSendInfo?.contactDetails.email : undefined}
        isSendReceiptOpen={isSendReceiptsOpen}
        setIsSendReceiptOpen={setIsSendReceiptsOpen}
      />
    </div>
  );
};

const TransactionPageWithCalendar = () => (
  <CalendarModeProvider>
    <TransactionPage />
  </CalendarModeProvider>
);

export default TransactionPageWithCalendar;
